/**
 * The storage consent (ADR-0016): the provider's sign-in opens in a small window so the job setup
 * stays where it is. The window is opened inside the person's click, before Paige is asked for the
 * consent URL, because a window opened after an await is blocked; when a blocker stops it anyway
 * the whole page goes to the consent and the API's page brings the person back (`returnTo`).
 */

/** What the API's consent page posts to the window that opened it (services/Paige.Api/Export/ConnectPage.cs). */
export const connectMessageType = 'paige-storage-connect';

/** A window for the consent, opened now; null when the browser refused one. */
export function openConsentWindow(): Window | null {
  return window.open('', 'paige-storage-connect', 'popup,width=520,height=720');
}

/**
 * Where "Back to Paige" on the consent page leads: this job's workbench on its setup, Export, with the
 * destination's sheet open (`?setup=export&destination=`; the review route and SendResultsCard honour
 * it). Used when the consent ran in this page, or the window was left open after it.
 */
export function returnPath(destinationId: string): string {
  const search = new URLSearchParams({ setup: 'export', destination: destinationId });
  return `${window.location.pathname}?${search.toString()}`;
}

/**
 * Calls `onDone` with what the consent page said, whichever way it arrives: a message from the window
 * Paige opened, or the same-origin broadcast the page also sends, because a provider's sign-in can cut
 * the window's link back to Paige (Cross-Origin-Opener-Policy). Returns the way to stop listening.
 */
export function onConsentDone(
  onDone: (ok: boolean, source: MessageEventSource | null) => void,
): () => void {
  const take = (event: MessageEvent<unknown>) => {
    const data = event.data as { type?: string; ok?: boolean } | null;
    if (data?.type === connectMessageType) onDone(Boolean(data.ok), event.source);
  };
  window.addEventListener('message', take);
  const channel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(connectMessageType);
  channel?.addEventListener('message', take);
  return () => {
    window.removeEventListener('message', take);
    channel?.close();
  };
}

/**
 * Sends the consent window (or, without one, this page) to the provider, and resolves when the
 * consent page says it is done or the window is closed. The result is only a hint: the caller reads
 * the destination again, which is the truth.
 */
export function awaitConsent(consent: Window | null, url: string): Promise<boolean> {
  if (!consent) {
    window.location.assign(url);
    return new Promise<boolean>(() => undefined);
  }
  consent.location.href = url;
  return new Promise<boolean>((resolve) => {
    const finish = (ok: boolean) => {
      stop();
      window.clearInterval(timer);
      resolve(ok);
    };
    // The window's own message, or the broadcast (no source) when the provider cut the window's link.
    const stop = onConsentDone(
      (ok, source) => (source === consent || source === null) && finish(ok),
    );
    const timer = window.setInterval(() => consent.closed && finish(false), 500);
  });
}
